import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { CheckCircle2Icon, PenLineIcon } from "lucide-react";
import { PortalDocuments } from "@/components/portal/portal-documents";
import { Link } from "@/core/lib/navigation";
import { portalDocumentsQuery, portalSignedQuery, portalSigningQuery } from "@/lib/portal-data";
import { Button } from "@/ui/components/ui/button";

export const Route = createFileRoute("/portal/documents")({
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(portalDocumentsQuery),
      context.queryClient.ensureQueryData(portalSigningQuery),
      context.queryClient.ensureQueryData(portalSignedQuery),
    ]),
  component: PortalDocumentsPage,
});

function PortalDocumentsPage() {
  const { data: documents } = useQuery(portalDocumentsQuery);
  const { data: signing } = useQuery(portalSigningQuery);
  const signingTasks = signing ?? [];
  const { data: signed } = useQuery(portalSignedQuery);
  const signedTasks = signed ?? [];

  return (
    <div className="space-y-8">
      <div className="space-y-1">
        <h1 className="font-heading font-semibold text-2xl tracking-tight">Documents</h1>
        <p className="text-muted-foreground text-sm">
          View and download documents shared with you.
        </p>
      </div>
      {signingTasks.length > 0 && (
        <section className="space-y-3">
          <h2 className="flex items-center gap-2 font-medium text-sm">
            <PenLineIcon className="size-4 text-amber-500" />
            Awaiting your signature
          </h2>
          <ul className="divide-y rounded-lg border border-amber-200 bg-amber-50/50 dark:border-amber-900/40 dark:bg-amber-950/20">
            {signingTasks.map((task) => (
              <li key={task.token} className="flex items-center gap-3 p-3 sm:p-4">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-sm">{task.title}</p>
                  <p className="truncate text-muted-foreground text-xs">
                    {task.typeName} · Signing as {task.role}
                  </p>
                </div>
                <Button asChild size="sm">
                  <Link href={`/portal/sign/${task.token}`}>Review &amp; sign</Link>
                </Button>
              </li>
            ))}
          </ul>
        </section>
      )}
      {signedTasks.length > 0 && (
        <section className="space-y-3">
          <h2 className="flex items-center gap-2 font-medium text-sm">
            <CheckCircle2Icon className="size-4 text-emerald-500" />
            Signed by you
          </h2>
          <ul className="divide-y rounded-lg border">
            {signedTasks.map((task) => (
              <li key={task.token} className="flex items-center gap-3 p-3 sm:p-4">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-sm">{task.title}</p>
                  <p className="truncate text-muted-foreground text-xs">
                    {[
                      `Signed as ${task.role}`,
                      task.signedAt ? new Date(task.signedAt).toLocaleDateString() : null,
                      task.completed ? "Everyone has signed" : "Waiting on others",
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                </div>
                <Button asChild size="sm" variant="outline">
                  <Link href={`/portal/sign/${task.token}`}>View</Link>
                </Button>
              </li>
            ))}
          </ul>
        </section>
      )}
      <section className="space-y-3">
        {(signingTasks.length > 0 || signedTasks.length > 0) && (
          <h2 className="font-medium text-sm">Your documents</h2>
        )}
        <PortalDocuments documents={documents ?? []} />
      </section>
    </div>
  );
}
